import { useState } from "react";
import { useEditor, useEditorState, EditorContent, type Editor } from "@tiptap/react";
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  RemoveFormatting,
  Baseline,
  Check,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { EMAIL_TEXT_COLORS, toHexColor } from "./emailBody";
import { emailEditorExtensions, editorPlainText } from "./emailEditorExtensions";

interface ToolbarButtonProps {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function ToolbarButton({ label, active, onClick, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      // Keep the editor selection when clicking the toolbar
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground/70 transition-colors hover:bg-muted hover:text-foreground",
        active && "bg-muted text-foreground"
      )}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      underline: e.isActive("underline"),
      bulletList: e.isActive("bulletList"),
      orderedList: e.isActive("orderedList"),
      color: toHexColor(e.getAttributes("textStyle").color as string | undefined),
    }),
  });

  const activeColor = EMAIL_TEXT_COLORS.find(c => c.value === state.color);
  const [colorOpen, setColorOpen] = useState(false);
  const applyColor = (value: string | null) => {
    const chain = editor.chain().focus();
    (value ? chain.setColor(value) : chain.unsetColor()).run();
    setColorOpen(false);
  };

  return (
    <div
      role="toolbar"
      aria-label="Formatting"
      className="flex flex-wrap items-center gap-0.5 border-b border-border px-1.5 py-1"
    >
      <ToolbarButton
        label="Bold (Ctrl+B)"
        active={state.bold}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Italic (Ctrl+I)"
        active={state.italic}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Underline (Ctrl+U)"
        active={state.underline}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <Underline className="h-4 w-4" />
      </ToolbarButton>

      <Popover open={colorOpen} onOpenChange={setColorOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Text color"
            title="Text color"
            onMouseDown={e => e.preventDefault()}
            className="inline-flex h-8 w-8 flex-col items-center justify-center rounded-md text-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
          >
            <Baseline className="h-4 w-4" style={{ color: activeColor?.value }} />
            <span
              className="mt-0.5 h-0.5 w-4 rounded-full"
              style={{ backgroundColor: activeColor?.value || "currentColor" }}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-1.5"
          align="start"
          onOpenAutoFocus={e => e.preventDefault()}
          onCloseAutoFocus={e => e.preventDefault()}
        >
          <div className="flex flex-col gap-0.5">
            <ColorOption
              name="Default"
              swatch="hsl(var(--foreground))"
              selected={!activeColor}
              onSelect={() => applyColor(null)}
            />
            {EMAIL_TEXT_COLORS.map(c => (
              <ColorOption
                key={c.value}
                name={c.name}
                swatch={c.value}
                selected={activeColor?.value === c.value}
                onSelect={() => applyColor(c.value)}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <span className="mx-1 h-5 w-px bg-border" aria-hidden />

      <ToolbarButton
        label="Bulleted list"
        active={state.bulletList}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Numbered list"
        active={state.orderedList}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>

      <span className="mx-1 h-5 w-px bg-border" aria-hidden />

      <ToolbarButton
        label="Clear formatting"
        onClick={() => editor.chain().focus().unsetAllMarks().run()}
      >
        <RemoveFormatting className="h-4 w-4" />
      </ToolbarButton>
    </div>
  );
}

function ColorOption({
  name,
  swatch,
  selected,
  onSelect,
}: {
  name: string;
  swatch: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onMouseDown={e => e.preventDefault()}
      onClick={onSelect}
      className="flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
    >
      <span
        className="h-3.5 w-3.5 rounded-full border border-border"
        style={{ background: swatch }}
      />
      <span className="flex-1" style={{ color: name === "Default" ? undefined : swatch }}>
        {name}
      </span>
      {selected ? <Check className="h-3.5 w-3.5" /> : <span className="h-3.5 w-3.5" />}
    </button>
  );
}

interface RichEmailEditorProps {
  /** Initial HTML. The editor owns the content after mount; remount (key) to load another draft. */
  html: string;
  editable: boolean;
  onChange?: (html: string, text: string) => void;
  onPlaceholderClick?: () => void;
  ariaLabel?: string;
}

export function RichEmailEditor({
  html,
  editable,
  onChange,
  onPlaceholderClick,
  ariaLabel = "Email body",
}: RichEmailEditorProps) {
  const editor = useEditor({
    extensions: emailEditorExtensions,
    content: html,
    editable,
    immediatelyRender: true,
    editorProps: {
      attributes: {
        "aria-label": ariaLabel,
        "aria-multiline": "true",
        role: "textbox",
        class: cn(
          "rich-email-content px-3 py-2 text-sm text-foreground focus:outline-none",
          editable ? "min-h-[240px]" : "min-h-[120px]"
        ),
      },
      handleClickOn: (_view, _pos, _node, _nodePos, event) => {
        const target = event.target as HTMLElement | null;
        if (onPlaceholderClick && target?.closest?.(".email-placeholder")) {
          onPlaceholderClick();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: e }) => onChange?.(e.getHTML(), editorPlainText(e)),
  });

  if (!editor) return null;

  return (
    <div
      className={cn(
        "rounded-md border",
        editable
          ? "border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
          : "border-border/50 bg-muted/40"
      )}
    >
      {editable && <Toolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
}

export default RichEmailEditor;
